import React, { useEffect, useState } from "react";
import { UserPlus, KeyRound, Trash2, Loader2, Copy, Check, X, RefreshCw, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Avatar, { ROLE_LABEL } from "../components/admin/Avatar";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/* What each role can do, in the owner's words rather than permission keys.
   Mirrors backend/auth/permissions.js — the server enforces it; this only
   explains it before someone is given access. */
const ROLES = [
  { id: "owner",      what: "Everything, including staff, settings and billing." },
  { id: "manager",    what: "Bookings, rates, guests, POS and reports. No settings or staff." },
  { id: "frontdesk",  what: "Bookings, check-in and check-out, guests and payments." },
  { id: "pos_staff",  what: "Point of sale only." },
  { id: "accountant", what: "Read-only financials and reports." },
];

const input = `w-full min-h-[44px] px-3.5 text-sm border border-gray-200 rounded-xl bg-white
  focus:outline-none focus:ring-2 focus:ring-amber-400`;

/* 12 characters from an alphabet with no look-alikes (no 0/O, 1/l/I), so a
   password read out over the phone arrives intact. */
function generatePassword() {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const bytes = new Uint32Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => alphabet[b % alphabet.length]).join("");
}

function PasswordField({ value, onChange }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  }
  return (
    <div className="flex gap-2">
      <input className={`${input} font-mono`} value={value} onChange={e => onChange(e.target.value)}
        minLength={8} required autoComplete="new-password" />
      <button type="button" onClick={() => onChange(generatePassword())} title="Generate a password" aria-label="Generate a password"
        className="shrink-0 w-11 min-h-[44px] rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 flex items-center justify-center">
        <RefreshCw size={15} />
      </button>
      <button type="button" onClick={copy} disabled={!value} title="Copy" aria-label="Copy password"
        className="shrink-0 w-11 min-h-[44px] rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 flex items-center justify-center disabled:opacity-40">
        {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />}
      </button>
    </div>
  );
}

function Sheet({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm sm:p-4"
      onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="w-11 h-11 -mr-2 flex items-center justify-center rounded-xl hover:bg-gray-100">
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        <div className="px-5 pb-5">{children}</div>
      </div>
    </div>
  );
}

export default function TeamPage() {
  const { authHeaders, me } = useAuth();
  const [staff, setStaff] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [resetFor, setResetFor] = useState(null);

  const [form, setForm] = useState({ fullName: "", email: "", mobile: "", role: "frontdesk", password: "" });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetMsg, setResetMsg] = useState({});

  async function load() {
    try {
      const res = await fetch(`${API}/staff`, { headers: authHeaders() });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      setStaff(j.data);
    } catch (err) { setError(err.message || "Couldn't load the team."); }
  }
  useEffect(() => { load(); }, []);

  const call = (path, method, body) => fetch(`${API}${path}`, {
    method, headers: { ...authHeaders(), "Content-Type": "application/json" },
    ...(body ? { body: JSON.stringify(body) } : {}),
  }).then(r => r.json());

  async function addMember(e) {
    e.preventDefault();
    setSaving(true); setFormError("");
    try {
      const j = await call("/staff", "POST", form);
      if (!j.success) throw new Error(j.error);
      setAdding(false);
      setForm({ fullName: "", email: "", mobile: "", role: "frontdesk", password: "" });
      await load();
    } catch (err) { setFormError(err.message || "Couldn't add them."); }
    finally { setSaving(false); }
  }

  async function changeRole(member, role) {
    if (role === member.role) return;
    const becomingOwner = role === "owner";
    const msg = becomingOwner
      ? `Make ${member.fullName} an owner? Owners can change staff, settings and billing — including removing you.`
      : `Change ${member.fullName} to ${ROLE_LABEL[role]}? It takes effect on their next click.`;
    if (!window.confirm(msg)) return;
    setBusyId(member._id); setError("");
    try {
      const j = await call(`/staff/${member._id}`, "PATCH", { role });
      if (!j.success) throw new Error(j.error);
      await load();
    } catch (err) { setError(err.message); }
    finally { setBusyId(null); }
  }

  async function remove(member) {
    if (!window.confirm(`Remove ${member.fullName}? They'll be signed out immediately and can't sign back in.`)) return;
    setBusyId(member._id); setError("");
    try {
      const j = await call(`/staff/${member._id}`, "DELETE");
      if (!j.success) throw new Error(j.error);
      await load();
    } catch (err) { setError(err.message); }
    finally { setBusyId(null); }
  }

  async function resetPassword(e) {
    e.preventDefault();
    setResetMsg({});
    try {
      const j = await call(`/staff/${resetFor._id}/reset-password`, "POST", { password: resetPw });
      if (!j.success) throw new Error(j.error);
      setResetMsg({ ok: `Done. Give ${resetFor.fullName.split(" ")[0]} the new password — it isn't shown again.` });
    } catch (err) { setResetMsg({ error: err.message }); }
  }

  if (!staff && !error) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-500" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4 sm:space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Team</h1>
          <p className="text-sm text-gray-500 mt-0.5">Who can sign in to this property, and what they can do.</p>
        </div>
        <button onClick={() => { setAdding(true); setFormError(""); setForm(f => ({ ...f, password: generatePassword() })); }}
          className="inline-flex items-center gap-1.5 min-h-[44px] px-4 text-sm font-semibold rounded-xl text-white
            bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
          <UserPlus size={16} /> Add team member
        </button>
      </div>

      {error && <p className="text-sm rounded-xl px-4 py-3 bg-rose-50 text-rose-700">{error}</p>}

      <ul className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-50">
        {(staff || []).map(m => {
          const self = me && String(me._id) === String(m._id);
          return (
            <li key={m._id} className={`p-4 sm:px-5 ${busyId === m._id ? "opacity-50" : ""}`}>
              <div className="flex items-start gap-3">
                <Avatar name={m.fullName} src={m.avatarUrl} size={44} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                    <span className="truncate">{m.fullName}</span>
                    {self && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">You</span>}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{m.email}{m.mobile ? ` · ${m.mobile}` : ""}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {m.lastLoginAt
                      ? `Last signed in ${new Date(m.lastLoginAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
                      : "Hasn't signed in yet"}
                  </p>
                </div>
              </div>

              {/* Your own row is read-only here: demoting or removing yourself
                  from the one screen that can undo it is a way to lock the
                  property out, not a feature. Your details live on Profile. */}
              <div className="flex items-center gap-2 mt-3 sm:pl-14 flex-wrap">
                {self ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 min-h-[44px]">
                    <ShieldCheck size={14} className="text-amber-500" /> {ROLE_LABEL[m.role]} · edit your details on My profile
                  </span>
                ) : (
                  <>
                    <select value={m.role} onChange={e => changeRole(m, e.target.value)} aria-label={`Role for ${m.fullName}`}
                      disabled={busyId === m._id}
                      className="min-h-[44px] px-3 text-xs font-medium border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
                      {ROLES.map(r => <option key={r.id} value={r.id}>{ROLE_LABEL[r.id]}</option>)}
                    </select>
                    <button onClick={() => { setResetFor(m); setResetPw(generatePassword()); setResetMsg({}); }}
                      className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 text-xs font-medium rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">
                      <KeyRound size={14} /> Reset password
                    </button>
                    <button onClick={() => remove(m)} disabled={busyId === m._id}
                      className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 text-xs font-medium rounded-xl text-rose-600 hover:bg-rose-50">
                      <Trash2 size={14} /> Remove
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <section className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4 sm:p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">What each role can do</h2>
        <dl className="space-y-2">
          {ROLES.map(r => (
            <div key={r.id} className="grid grid-cols-[96px_1fr] gap-3 text-xs">
              <dt className="font-semibold text-gray-700">{ROLE_LABEL[r.id]}</dt>
              <dd className="text-gray-500">{r.what}</dd>
            </div>
          ))}
        </dl>
      </section>

      {adding && (
        <Sheet title="Add team member" onClose={() => setAdding(false)}>
          <form onSubmit={addMember} className="space-y-3.5">
            <label className="block"><span className="block text-xs font-semibold text-gray-500 mb-1.5">Full name</span>
              <input className={input} value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} required /></label>
            <label className="block"><span className="block text-xs font-semibold text-gray-500 mb-1.5">Email — their sign-in</span>
              <input className={input} type="email" inputMode="email" autoComplete="off" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} required /></label>
            <label className="block"><span className="block text-xs font-semibold text-gray-500 mb-1.5">Mobile (optional)</span>
              <input className={input} type="tel" inputMode="tel" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} /></label>
            <label className="block"><span className="block text-xs font-semibold text-gray-500 mb-1.5">Role</span>
              <select className={input} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                {ROLES.map(r => <option key={r.id} value={r.id}>{ROLE_LABEL[r.id]}</option>)}
              </select>
              <span className="block text-[11px] text-gray-400 mt-1">{ROLES.find(r => r.id === form.role)?.what}</span>
            </label>
            <div><span className="block text-xs font-semibold text-gray-500 mb-1.5">Starting password</span>
              <PasswordField value={form.password} onChange={password => setForm({ ...form, password })} />
              <span className="block text-[11px] text-gray-400 mt-1">Copy it now and pass it on. They can change it from My profile.</span>
            </div>
            {formError && <p className="text-xs rounded-xl px-3 py-2.5 bg-rose-50 text-rose-700">{formError}</p>}
            <button type="submit" disabled={saving}
              className="w-full inline-flex items-center justify-center gap-1.5 min-h-[48px] text-sm font-semibold rounded-xl text-white
                bg-gradient-to-r from-amber-500 to-orange-500 disabled:opacity-50">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Add to team
            </button>
          </form>
        </Sheet>
      )}

      {resetFor && (
        <Sheet title={`Reset password — ${resetFor.fullName}`} onClose={() => setResetFor(null)}>
          <form onSubmit={resetPassword} className="space-y-3.5">
            <p className="text-xs text-gray-500">Their current password stops working. Copy the new one and pass it on.</p>
            <PasswordField value={resetPw} onChange={setResetPw} />
            {resetMsg.error && <p className="text-xs rounded-xl px-3 py-2.5 bg-rose-50 text-rose-700">{resetMsg.error}</p>}
            {resetMsg.ok && <p className="text-xs rounded-xl px-3 py-2.5 bg-emerald-50 text-emerald-700">{resetMsg.ok}</p>}
            {!resetMsg.ok ? (
              <button type="submit" className="w-full min-h-[48px] text-sm font-semibold rounded-xl text-white bg-gray-900 hover:bg-gray-800">
                Set new password
              </button>
            ) : (
              <button type="button" onClick={() => setResetFor(null)}
                className="w-full min-h-[48px] text-sm font-semibold rounded-xl border border-gray-200 text-gray-700">Done</button>
            )}
          </form>
        </Sheet>
      )}
    </div>
  );
}
